import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, XCircle, Certificate, ArrowSquareOut, PencilLine, ShieldCheck } from "@phosphor-icons/react";
import { getTheme } from "@/lib/certificateTemplates";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function VerifyCertificate() {
  const { cert_no } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    axios.get(`${BASE}/api/public/verify/${cert_no}`)
      .then((r) => setState({ loading: false, data: r.data, error: null }))
      .catch((e) => setState({ loading: false, data: null, error: e?.response?.status === 404 ? "Certificate not found" : "Verification failed" }));
  }, [cert_no]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-lg space-y-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 mb-2">
            <div className="w-9 h-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-bold" style={{ fontFamily: "Manrope" }}>B</div>
            <div>
              <div className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>Bilinguist</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Certificate Verification</div>
            </div>
          </div>
        </div>

        {state.loading && <Card><CardContent className="p-8 text-center text-muted-foreground">Verifying…</CardContent></Card>}

        {state.error && (
          <Card className="border-destructive"><CardContent className="p-8 text-center" data-testid="verify-invalid">
            <XCircle size={48} weight="duotone" className="mx-auto text-destructive" />
            <h1 className="text-2xl font-bold mt-3" style={{ fontFamily: "Manrope" }}>Not Verified</h1>
            <p className="text-sm text-muted-foreground mt-1">{state.error}</p>
            <div className="mt-4 font-mono text-xs text-muted-foreground">№ {cert_no}</div>
          </CardContent></Card>
        )}

        {state.data && (() => {
          const t = getTheme(state.data.template);
          return (
            <Card className="overflow-hidden" style={{ borderColor: t.accent, borderWidth: 2 }} data-testid="verify-valid">
              <div className="h-2" style={{ background: `linear-gradient(90deg, ${t.accent}, ${t.accentLight})` }} />
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-3"
                  style={{ background: t.accent, color: t.bgFrom, fontSize: 28 }}>
                  {t.seal}
                </div>
                <div className="flex items-center justify-center gap-2 text-emerald-600 dark:text-emerald-400">
                  <CheckCircle size={18} weight="fill" />
                  <span className="text-xs uppercase tracking-[0.22em] font-bold">Verified Authentic</span>
                </div>
                <h1 className="text-3xl font-bold mt-3" style={{ fontFamily: "Manrope" }}>{state.data.student_name}</h1>
                <div className="text-sm text-muted-foreground mt-1">has successfully completed</div>
                <div className="text-lg font-semibold mt-2" style={{ fontFamily: "Manrope" }}>{state.data.title}</div>
                <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
                  <Badge className="capitalize font-bold" style={{ backgroundColor: t.accent, color: t.bgFrom }}>
                    {t.label} · {t.tag}
                  </Badge>
                  {state.data.level && <Badge variant="outline" className="font-mono">Level {state.data.level}</Badge>}
                </div>
                <div className="mt-6 pt-6 border-t border-border grid grid-cols-2 gap-4 text-left">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Certificate №</div>
                    <div className="font-mono font-bold text-sm">{state.data.certificate_no}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Issued</div>
                    <div className="font-mono font-bold text-sm">{state.data.issued_date}</div>
                  </div>
                </div>
                {state.data.signed_by && (
                  <div data-testid="signed-line" className="mt-5 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md"
                    style={{ background: t.bgFrom, border: `1px solid ${t.accentLight}` }}>
                    <ShieldCheck size={16} weight="fill" style={{ color: t.accent }} />
                    <span className="text-xs" style={{ color: t.textOn }}>
                      Digitally signed by <span className="font-semibold">{state.data.signed_by}</span> on{" "}
                      <span className="font-mono">
                        {new Date(state.data.signed_on).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                      </span>
                    </span>
                  </div>
                )}
                <div className="mt-4 text-[10px] uppercase tracking-widest text-muted-foreground flex items-center justify-center gap-1">
                  Issued by {state.data.issued_by} <ArrowSquareOut size={10} />
                </div>
              </CardContent>
            </Card>
          );
        })()}

        <div className="text-center text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          Bilinguist School OS · Public Verification Service
        </div>
      </div>
    </div>
  );
}
